import { useQuery } from '@tanstack/react-query';
import apiService from '../services/apiService';

export interface GameLaunchMaintenanceData {
  gameLaunchMaintenance: boolean;
  casinoLaunchMaintenance: boolean;
  slotLaunchMaintenance: boolean;
  casinoBlocked: boolean;
  slotBlocked: boolean;
}

function envTruthy(v: string | undefined): boolean {
  if (!v) return false;
  const s = String(v).toLowerCase().trim();
  return s === '1' || s === 'true' || s === 'yes';
}

/**
 * Admin-controlled via settings (game_launch_maintenance, casino_launch_maintenance, slot_launch_maintenance).
 * Env vars still override as emergency: REACT_APP_GAME_LAUNCH_MAINTENANCE, etc.
 */
export function useGameLaunchMaintenance() {
  const envGlobal = envTruthy(process.env.REACT_APP_GAME_LAUNCH_MAINTENANCE);
  const envCasino = envTruthy(process.env.REACT_APP_CASINO_LAUNCH_MAINTENANCE);
  const envSlot = envTruthy(process.env.REACT_APP_SLOT_LAUNCH_MAINTENANCE);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['config', 'game-launch-maintenance'],
    queryFn: async (): Promise<GameLaunchMaintenanceData> => {
      const res: any = await apiService.get('/api/config/game-launch-maintenance');
      const d = res?.data ?? res; // backend returns { status, data: { casinoBlocked, ... } }
      return {
        gameLaunchMaintenance: !!d?.gameLaunchMaintenance,
        casinoLaunchMaintenance: !!d?.casinoLaunchMaintenance,
        slotLaunchMaintenance: !!d?.slotLaunchMaintenance,
        casinoBlocked: !!d?.casinoBlocked,
        slotBlocked: !!d?.slotBlocked,
      };
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const casinoBlocked = envGlobal || envCasino || (!isError && (data?.casinoBlocked ?? false));
  const slotBlocked = envGlobal || envSlot || (!isError && (data?.slotBlocked ?? false));

  return {
    casinoBlocked,
    slotBlocked,
    isLoading,
    isError,
    /** Raw API flags (no env override) */
    fromApi: data,
  };
}
